import { userApi, type RegisterRequest } from './api';
import type { UserProfileData } from '@/shared/services/userApiService';
import { UserRole } from '@/shared/types';
import { logger, CookieManager, storageUtils } from '@/shared/utils';
import { STORAGE_KEYS } from '@/shared/config';
import { ENV } from '@/config/environment';
import { apiService } from '@/shared/services/apiService';

import type {
  User, 
  LoginRequest, 
  UpdateUserRequest,
  UserConfig
} from '@/shared/types';

export const LOCAL_DEV_USER: User = {
  id: 'local-dev-user',
  username: 'local-dev',
  email: 'local-dev@localhost',
  project_id: ENV.project.id,
  first_name: 'Local',
  last_name: 'Developer',
  is_active: true,
  is_admin: true,
  is_god: true,
  data: {
    bio: 'Local development user with all frontend pages unlocked.',
  },
};

/**
 * Maps a backend UserProfileData response to the frontend User shape.
 * Centralized so created_at/updated_at (and any future field) only need to
 * be wired up once, instead of independently in every call site that builds
 * a User object from a profile response.
 */
function mapUserProfileToUser(userData: UserProfileData, previousId?: string): User {
  const userDataObj = userData.data || {};
  if (userData.bio !== undefined) {
    userDataObj.bio = userData.bio;
  }

  return {
    id: previousId || userData.username || '',
    username: userData.username,
    email: userData.email,
    project_id: userData.project_id,
    first_name: userData.first_name || '',
    last_name: userData.last_name || '',
    is_active: userData.is_active,
    is_admin: userData.is_admin,
    is_god: userData.is_god,
    created_at: userData.created_at,
    updated_at: userData.updated_at,
    data: userDataObj,
  };
}

export class UserService {
  private user: User | null = null;
  private token: string | null = null;
  private config: UserConfig;

  constructor(config?: Partial<UserConfig>) {
    this.config = {
      autoRedirect: true,
      redirectPaths: {
        login: '/login',
        logout: '/login',
        dashboard: '/dashboard'
      },
      tokenStorageKey: 'auth_token',
      userStorageKey: 'user_data',
      ...config
    };
    
    this.loadFromCookies();
  }

  /**
   * Authenticate user and store session
   */
  async login(credentials: LoginRequest): Promise<User> {
    try {
      // Detect if input is email or username and prepare the correct request
      const loginRequest: LoginRequest = {
        password: credentials.password
      };

      // Check if the input is an email (contains @ symbol)
      const inputValue = credentials.email || credentials.username || '';
      if (this.isEmail(inputValue)) {
        loginRequest.email = inputValue;
      } else {
        loginRequest.username = inputValue;
      }

      // Call API to login
      // apiService returns ApiResponse<T>, so loginResponseWrapper is ApiResponse<AuthResponseData>
      const loginResponseWrapper = await userApi.login(loginRequest);

      if (!loginResponseWrapper.success || !loginResponseWrapper.data) {
        throw new Error(loginResponseWrapper.error?.message || 'Login failed');
      }
      
      // Store session token (7-day expiration, managed by backend session)
      // loginResponseWrapper.data is AuthResponseData { token, expires_at }
      this.setToken(String(loginResponseWrapper.data.token));
      
      // Fetch user profile
      // apiService returns ApiResponse<T>, so userProfileResponseWrapper is ApiResponse<UserProfileData>
      const userProfileResponseWrapper = await userApi.getCurrentUser();

      if (!userProfileResponseWrapper.success || !userProfileResponseWrapper.data) {
        throw new Error(userProfileResponseWrapper.error?.message || 'Failed to fetch user profile');
      }

      // userProfileResponseWrapper.data is UserProfileData
      const userProfile = userProfileResponseWrapper.data;
      const user = mapUserProfileToUser(userProfile as UserProfileData);

      this.setUser(user);

      logger.info('Login successful', 'UserService', { username: user.username });
      return user;
    } catch (error) {
      this.clearUserData();
      throw error;
    }
  }

  /**
   * Helper method to detect if input is an email
   */
  private isEmail(input: string): boolean {
    return input.includes('@') && input.includes('.');
  }

  /**
   * Register new user
   */
  async register(userData: RegisterRequest): Promise<void> {
    // apiService returns ApiResponse<T>, so registrationResponseWrapper is ApiResponse<RegistrationResponseData>
    const registrationResponseWrapper = await userApi.register(userData);
    
    logger.info('Registration response received', 'UserService', { 
      success: registrationResponseWrapper.success 
    });
    
    if (!registrationResponseWrapper.success) {
      throw new Error(registrationResponseWrapper.error?.message || 'Registration failed');
    }
    
    logger.info('Registration successful', 'UserService', {
      userId: registrationResponseWrapper.data?.user_id
    });
  }

  /**
   * Logout user and clear session
   */
  async logout(): Promise<void> {
    try {
      // Call API to logout (if authenticated)
      if (this.isAuthenticated()) {
        await userApi.logout();
      }
    } catch (error) {
      logger.error('Logout API call failed', 'UserService', { error });
      // Continue with local logout even if API fails
    } finally {
      this.clearUserData();
      logger.info('User logged out', 'UserService');
    }
  }

  /**
   * Update user profile
   */
  async updateUser(updateData: UpdateUserRequest): Promise<User> {
    logger.debug('Starting user update', 'UserService', { updateData });
    
    if (!this.isAuthenticated()) {
      logger.warn('User not authenticated', 'UserService');
      throw new Error('User not authenticated');
    }

    try {
      logger.debug('Calling update API', 'UserService');
      // Update user via API
      // apiService returns ApiResponse<T>, so updateResponseWrapper is ApiResponse<UpdateUserData>
      const updateResponseWrapper = await userApi.updateUser(updateData);
      logger.debug('Update response received', 'UserService', { success: updateResponseWrapper.success });
      
      if (!updateResponseWrapper.success) {
        logger.error('Update failed', 'UserService', { error: updateResponseWrapper.error });
        throw new Error(updateResponseWrapper.error?.message || 'Update failed');
      }
      
      logger.debug('Refreshing user data', 'UserService');
      // Fetch updated user data
      // apiService returns ApiResponse<T>, so userProfileResponseWrapper is ApiResponse<UserProfileData>
      const userProfileResponseWrapper = await userApi.getCurrentUser();
      logger.debug('User refresh response received', 'UserService', { success: userProfileResponseWrapper.success });
      
      if (!userProfileResponseWrapper.success || !userProfileResponseWrapper.data) {
        logger.error('User refresh failed', 'UserService', { error: userProfileResponseWrapper.error });
        throw new Error(userProfileResponseWrapper.error?.message || 'Failed to fetch updated user profile');
      }

      // userProfileResponseWrapper.data is UserProfileData
      const userData = userProfileResponseWrapper.data;
      const updatedUser = mapUserProfileToUser(userData, this.user?.id);

      this.setUser(updatedUser);
      
      logger.info('User updated successfully', 'UserService', { 
        username: updatedUser.username,
        userId: updatedUser.id,
        message: updateResponseWrapper.message || 'Profile updated successfully'
      });
      return updatedUser;
    } catch (error) {
      logger.error('Update user failed', 'UserService', { error });
      throw error;
    }
  }

  /**
   * Delete user account - Deletes current user's account
   */
  async deleteAccount(): Promise<void> {
    if (!this.isAuthenticated()) {
      throw new Error('Authentication required to delete account');
    }

    try {
      const { userApiService } = await import('@/shared/services/userApiService');
      const response = await userApiService.deleteAccount();
      
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete account');
      }
      
      // Clear user data after successful deletion
      this.clearUserData();
      logger.info('Account deleted successfully', 'UserService');
    } catch (error) {
      logger.error('Failed to delete account', 'UserService', { error });
      throw error;
    }
  }

  /**
   * Change user password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    if (!this.isAuthenticated()) {
      throw new Error('User not authenticated');
    }

    if (!currentPassword || currentPassword.trim().length === 0) {
      throw new Error('Current password is required');
    }

    if (!newPassword || newPassword.trim().length === 0) {
      throw new Error('New password is required');
    }

    if (newPassword.length < 6) {
      throw new Error('New password must be at least 6 characters long');
    }

    if (currentPassword === newPassword) {
      throw new Error('New password must be different from current password');
    }

    try {
      // apiService returns ApiResponse<T>, so responseWrapper is ApiResponse<void>
      const responseWrapper = await userApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword
      });
      
      if (!responseWrapper.success) {
        throw new Error(responseWrapper.error?.message || 'Password change failed');
      }
      
      logger.info('Password changed successfully', 'UserService');
    } catch (error) {
      logger.error('Password change failed', 'UserService', { error });
      throw error;
    }
  }

  /**
   * Get current user
   */
  getUser(): User | null {
    if (ENV.app.localDevMode) {
      return this.user ?? LOCAL_DEV_USER;
    }
    return this.user;
  }

  /**
   * Check if user is authenticated
   * Also checks localStorage as fallback for token and user
   */
  isAuthenticated(): boolean {
    if (ENV.app.localDevMode) {
      return true;
    }

    // Check if we have token (from instance, cookies, or localStorage)
    const tokenFromCookies = CookieManager.get(this.config.tokenStorageKey);
    const tokenFromStorage = storageUtils.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const token = this.token || tokenFromCookies || tokenFromStorage;
    
    // Check if we have user (from instance, cookies, or localStorage)
    let user = this.user;
    if (!user) {
      const userDataFromCookies = CookieManager.get(this.config.userStorageKey);
      const userDataFromStorage = storageUtils.getItem(STORAGE_KEYS.USER_DATA);
      const userData = userDataFromCookies || userDataFromStorage;
      if (userData) {
        try {
          user = typeof userData === 'string' ? JSON.parse(userData) : userData;
        } catch {
          // Ignore parse errors
        }
      }
    }
    
    // User is authenticated if we have both token and user
    return !!token && !!user;
  }

  /**
   * Get authentication token
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: UserRole): boolean {
    if (ENV.app.localDevMode) {
      return true;
    }

    if (!this.user) return false;
    
    // Check if user is admin or god (has all roles)
    const isAdmin = this.user.is_admin === true || this.user.is_god === true;
    if (isAdmin) return true;
    
    // For now, map admin/god to admin role, regular users to user role
    if (role === UserRole.ADMIN) return isAdmin;
    if (role === UserRole.USER) return !isAdmin;
    
    return false;
  }

  /**
   * Refresh user data from server
   */
  async refreshUser(): Promise<User | null> {
    if (ENV.app.localDevMode) {
      this.user = LOCAL_DEV_USER;
      return LOCAL_DEV_USER;
    }

    if (!this.isAuthenticated()) {
      return null;
    }

    try {
      // apiService returns ApiResponse<T>, so userProfileResponseWrapper is ApiResponse<UserProfileData>
      const userProfileResponseWrapper = await userApi.getCurrentUser();
      
      if (!userProfileResponseWrapper.success || !userProfileResponseWrapper.data) {
        throw new Error(userProfileResponseWrapper.error?.message || 'Failed to fetch user profile');
      }

      // userProfileResponseWrapper.data is UserProfileData
      const userData = userProfileResponseWrapper.data;
      const user = mapUserProfileToUser(userData, this.user?.id);

      this.setUser(user);
      return user;
    } catch (error) {
      logger.error('Failed to refresh user data', 'UserService', { error });
      // If refresh fails, user might be logged out
      this.clearUserData();
      return null;
    }
  }

  /**
   * Store authentication token
   */
  private setToken(token: string): void {
    this.token = token;
    // Store in cookies (primary storage) with secure flags
    CookieManager.set(this.config.tokenStorageKey, token, {
      expires: 7, // 7 days
      path: '/',
      secure: true, // Always secure (will be true in production via CookieManager)
      sameSite: 'strict' // Stricter than 'lax' for better security
    });
    // Also store in localStorage as backup (for apiService compatibility)
    storageUtils.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    // Set authorization header for API requests
    // Note: apiService handles this internally
  }

  /**
   * Store user data
   */
  private setUser(user: User): void {
    this.user = user;
    CookieManager.set(this.config.userStorageKey, JSON.stringify(user), {
      expires: 7, // 7 days
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'lax'
    });
  }

  /**
   * Load user session from cookies
   * Also checks localStorage as fallback for compatibility
   */
  private loadFromCookies(): void {
    // Try cookies first (primary storage)
    let token = CookieManager.get(this.config.tokenStorageKey);
    let userData = CookieManager.get(this.config.userStorageKey);

    // Fallback to localStorage if cookies not available
    if (!token) {
      token = storageUtils.getItem(STORAGE_KEYS.AUTH_TOKEN);
    }
    if (!userData) {
      const userDataStr = storageUtils.getItem(STORAGE_KEYS.USER_DATA);
      userData = userDataStr ? userDataStr : null;
    }

    if (token) {
      this.token = token;
      // Also ensure it's in localStorage for apiService compatibility
      if (!storageUtils.getItem(STORAGE_KEYS.AUTH_TOKEN)) {
        storageUtils.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      }
    }

    if (userData) {
      try {
        this.user = typeof userData === 'string' ? JSON.parse(userData) : userData;
        // Also ensure it's in cookies for consistency
        if (!CookieManager.get(this.config.userStorageKey)) {
          CookieManager.set(this.config.userStorageKey, JSON.stringify(this.user), {
            expires: 7,
            path: '/',
            secure: window.location.protocol === 'https:',
            sameSite: 'lax'
          });
        }
      } catch (error) {
        logger.error('Failed to parse user data', 'UserService', { error });
        this.clearUserData();
      }
    }
  }

  /**
   * Clear all user data and session
   */
  private clearUserData(): void {
    this.user = null;
    this.token = null;
    CookieManager.remove(this.config.tokenStorageKey, { path: '/' });
    CookieManager.remove(this.config.userStorageKey, { path: '/' });
    // Clear refresh token if it exists
    storageUtils.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    // Clear authorization header
    // Note: apiService handles this internally
    // Drop cached GET responses so the next user/session never sees stale data
    apiService.clearCache();
  }
}

// Export singleton instance with default config
export const userService = new UserService();
